import { LoyaltyProgram } from './customers.types';
import {
  ChangedAttributes,
  EntityTypes,
  SoftDeleteFlag,
  Timestamps,
  Versioned,
} from './shared.types';
import { Tax } from './taxes.types';
import { StockBreakdown } from './inventory.types';
import { ProductDiscountChange } from './discounts.types';

export interface Barcode {
  value: string | number;
  type: string | number;
  description?: string | null | undefined;
}

export interface ProductFraction extends Timestamps, Versioned {
  parent_id: string;
  fraction_id: string;
  fraction: number;
  fraction_name: string;
  parent_name: string;
  merchant_id: string;
  /**
   * Cognito sub / employee id of the user who soft-deleted this fraction.
   * Optional for backwards compatibility with legacy rows that only have `deleted_at`.
   */
  deleted_by_id?: string | null;
  /**
   * Human-readable name of the user who soft-deleted this fraction.
   */
  deleted_by_name?: string | null;
}

export type StoreProductFraction = Product &
  StoreProductAttributes &
  ProductFraction;

export interface Product extends Timestamps {
  id: string;
  name: string;
  description: string;
  image: string;
  barcode: string | number;
  quantity_in_unit_package: number;
  global_sku: number;
  weight_kg: number;
  unit: string | number;
  unit_of_measurement: string;
  category_id?: string | null;
  merchant_id: string;
  fraction: number;
  parent_id: string;
  is_parent: boolean;
  product_fractions: ProductFraction[];
  taxes: string[] | Tax[];
  barcodes?: Barcode[] | null;
  /** ISO 3166-1 alpha-2 codes of the countries the product is sold in.
   * Optional for backwards compatibility; missing = []. */
  countries?: string[] | null;
  version: number;
  /** Id of the user who made the latest change (AGENTS.md §4.4.1). Optional for backwards compatibility. */
  updated_by_id?: string | null;
  /** Name of the user who made the latest change. Optional for backwards compatibility. */
  updated_by_name?: string | null;
  /**
   * Cognito sub / employee id of the user who soft-deleted this product.
   * Optional for backwards compatibility with legacy rows that only have `deleted_at`.
   */
  deleted_by_id?: string | null;
  /**
   * Human-readable name of the user who soft-deleted this product.
   */
  deleted_by_name?: string | null;
}

export interface StoreProductAttributes extends Timestamps {
  id: string;
  store_sku: string;
  store_id: string;
  unit_of_measurement: string;
  /** Sellable stock in parent units: on hand minus reserved, pending and blocked. */
  inventory: number;
  /**
   * The stock buckets behind `inventory`, set once the stock row has
   * buckets. Optional for backwards compatibility; missing = all of the
   * stock is sellable (on hand = inventory).
   */
  stock_breakdown?: StockBreakdown | null;
  /**
   * Version of the stock row (CURRENT_INVENTORY) that `inventory` and
   * `stock_breakdown` were mirrored from. Optional for backwards
   * compatibility; missing = 0 (any versioned snapshot replaces it).
   */
  stock_version?: number | null;
  /**
   * When the cost last changed other than by service-inventory's receipt
   * valuation (a hand edit, a purchase-order receive): a valuation computed
   * before it never overwrites it. Optional for backwards compatibility;
   * missing = never.
   */
  cost_changed_at?: string | null;
  /**
   * The stock row version of the last cost service-inventory set
   * (`inventory_valuation`). Optional for backwards compatibility;
   * missing = 0.
   */
  cost_stock_version?: number | null;
  is_available_for_sale: boolean;
  is_manually_controlled: boolean;
  is_sold_online: boolean;
  is_available_in_store: boolean;
  is_sold_by_range: boolean;
  should_display_cost_on_pos: boolean;
  is_sold_by_weight: boolean;
  loyalty_programs: string[] | LoyaltyProgram[];
  price: number;
  cost: number;
  low_stock_threshold: number;
  /**
   * Stock level at which the product must be re-ordered (parent products).
   * Optional for backwards compatibility; missing = no reorder warning.
   */
  reorder_level?: number | null;
  store_prefix: string;
  is_parent: boolean;
  merchant_id: string;
  version: number;
  /**
   * Read-only: the store product row's own version, used as its optimistic
   * lock (`version` above is the product's). Missing on legacy rows.
   */
  row_version?: number | null;
  /** Id of the user who made the latest change (AGENTS.md §4.4.1). Optional for backwards compatibility. */
  updated_by_id?: string | null;
  /** Name of the user who made the latest change. Optional for backwards compatibility. */
  updated_by_name?: string | null;
  store_product_fractions: StoreProductFraction[];
  max_selling_price: number;
  min_selling_price: number;
  profit_margin?: number | null;
  max_profit_margin?: number | null;
  min_profit_margin?: number | null;
  popularity_score?: number | null;
  last_counted_at?: string | null;
  risk_score?: number | null;
  /**
   * Opt-in clearance flag: when true, this store product may be priced and
   * sold below its cost (e.g. discounted stock that is about to expire).
   * The domain model stops flooring price/min_selling_price at cost and
   * keeps the product available for sale at a loss. Defaults to false.
   */
  allow_below_cost_sale?: boolean | null;
  /** Changes whenever the product's discount configuration/assignment changes. */
  discount_updated_at?: string | null;
  /**
   * Days before a purchase-order expiry date the expiry warning starts
   * (30, 14, 7 or 3). Optional for backwards compatibility; missing = 14.
   */
  expiry_warning_days?: number | null;
}

export type StoreProduct = Product & StoreProductAttributes;

export interface RealTimeProductEventMessage {
  storeId: string;
  product: StoreProduct;
  deduplication_id: string;
}

export interface GetProducts {
  merchant_id: string;
  is_parent?: boolean;
  cursor?: string;
  limit?: number;
  include_deleted?: SoftDeleteFlag;
  include_fractions?: boolean;
}

export interface CreateProduct {
  creator_id: string;
  name: string;
  description: string;
  image: string;
  barcode: string | number;
  quantity_in_unit_package: number;
  weight_kg: number;
  unit: string | number;
  category_id?: string;
  merchant_id: string;
  unit_of_measurement: string;
  taxes: string[];
  barcodes?: Barcode[];
  /** Countries the product is sold in (ISO codes). Omitted = the
   * merchant's country. */
  countries?: string[];
}

export interface UpdateProduct {
  id: string;
  merchant_id: string;
  updator_id: string;
  unit_of_measurement?: string;
  name?: string;
  description?: string;
  image?: string;
  barcode?: string | number;
  quantity_in_unit_package?: number;
  global_sku?: number;
  weight_kg?: number;
  unit?: string | number;
  category_id?: string;
  add_taxes?: string[];
  remove_taxes?: string[];
  add_barcodes?: Barcode[];
  remove_barcodes?: Barcode[];
}

export interface GetStoreProducts {
  store_id: string;
  is_parent?: boolean;
  cursor?: string;
  limit?: number;
  include_deleted?: SoftDeleteFlag;
}

export interface GetStoreProduct {
  store_id: string;
  id: string;
  include_deleted?: SoftDeleteFlag;
}

export interface GetStoreProductsByMerchant extends GetStoreProducts {
  merchant_id: string;
}

export type StoreProductExportTrigger = 'manual' | 'scheduled';

export interface CreateStoreProductsExportJob {
  include_deleted?: boolean;
  requested_by?: string;
  triggered_by?: StoreProductExportTrigger;
}

export interface StoreProductsExportJobResponse {
  job_id: string;
  s3_key: string;
  requested_at: string;
  triggered_by: StoreProductExportTrigger;
  include_deleted: boolean;
  requested_by?: string;
}

export interface StoreProductExportRecord {
  key: string;
  file_name: string;
  job_id: string;
  requested_at: string;
  triggered_by: StoreProductExportTrigger;
  include_deleted: boolean;
  download_url: string;
  requested_by?: string;
  size_in_bytes?: number;
  last_modified?: string;
  /**
   * Set when the signed inventory-count PDF generated alongside the CSV
   * exists in S3. Absent for exports created before PDF generation shipped
   * or when the PDF could not be generated.
   */
  pdf_key?: string;
  pdf_download_url?: string;
  pdf_size_in_bytes?: number;
}

export interface StoreProductExportsResponse {
  exports: StoreProductExportRecord[];
  next_cursor?: string;
}

export interface ExportStoreProductsSQSMessage {
  job_id: string;
  merchant_id: string;
  store_id: string;
  include_deleted?: boolean;
  triggered_by: StoreProductExportTrigger;
  requested_at: string;
  requested_by?: string;
}

export interface CreateStoreProduct {
  creator_id: string;
  product_id: string;
  store_id: string;
  inventory: number;
  is_available_for_sale: boolean;
  is_sold_by_range: boolean;
  max_selling_price: number;
  min_selling_price: number;
  is_manually_controlled: boolean;
  is_sold_by_weight: boolean;
  is_sold_online: boolean;
  loyalty_programs: string[];
  price: number;
  cost: number;
  low_stock_threshold: number;
  merchant_id: string;
}

export interface GetStoreProductByMerchant {
  merchant_id: string;
  store_id: string;
  id: string;
  include_deleted?: SoftDeleteFlag;
}

export interface GetProduct {
  merchant_id: string;
  id: string;
  include_deleted?: SoftDeleteFlag;
}

export interface UpdateStoreProduct {
  id: string;
  store_id: string;
  merchant_id: string;
  updator_id: string;
  inventory?: number;
  is_available_for_sale?: boolean;
  is_sold_by_range?: boolean;
  max_selling_price?: number;
  min_selling_price?: number;
  is_manually_controlled?: boolean;
  is_sold_by_weight?: boolean;
  is_sold_online?: boolean;
  add_loyalty_programs?: string[];
  remove_loyalty_programs?: string[];
  price?: number;
  cost?: number;
  low_stock_threshold?: number;
  is_available_in_store?: boolean;
  should_display_cost_on_pos?: boolean;
  /** 30, 14, 7 or 3 days; see `StoreProductAttributes.expiry_warning_days`. */
  expiry_warning_days?: number | null;
  /** Stock level at which to re-order; null clears it. */
  reorder_level?: number | null;
  /**
   * @deprecated No longer read. Cost/price changes now send an idempotent
   * recompute trigger to the reports service instead of a value delta, so
   * there is nothing to skip. Accepted for backwards compatibility only.
   */
  skip_inventory_value_delta?: boolean;
  last_counted_at?: string;
  risk_score_increment?: number;
  /**
   * Opt-in clearance flag: when true the product may be priced and sold
   * below cost (expiring stock discounts). Turning it off re-floors the
   * price and min selling price at the current cost.
   */
  allow_below_cost_sale?: boolean;
  /** Human-readable audit reason for this update. */
  change_reason?: string;
  /**
   * When the change was made at its source (ISO 8601), e.g. on a till that
   * was offline: the change records are dated with it. Optional for
   * backwards compatibility; missing = the time of the request.
   */
  occurred_at?: string;
  /**
   * The price the caller saw before changing it. When set and the stored
   * price differs, nothing is written and the request is refused with 409:
   * a newer price is never overwritten by an older edit (POS offline price
   * changes). Optional for backwards compatibility; missing = no check.
   */
  expected_price?: number;
  /** Immutable discount audit metadata supplied by the discounts service. */
  discount_change?: ProductDiscountChange;
  /**
   * Where a cost update originated. PURCHASE_ORDER_RECEIVE marks the
   * weighted-average-cost recompute that follows receiving a purchase
   * order; the stock revaluation it causes is already offset by Supplier
   * Due in the store valuation, so no cost-revaluation impact is recorded
   * for it. Manual cost edits omit this field.
   */
  cost_update_origin?: CostUpdateOrigin;
  /**
   * A purchase-order receive: the products service computes the weighted
   * average cost from the store product's current cost inside its locked
   * write, so a concurrent cost change is never overwritten. Takes
   * precedence over `cost`. Optional: older callers send `cost`.
   */
  weighted_receive?: WeightedReceive | null;
  /**
   * A transfer receipt valued by service-inventory inside its stock
   * transaction (docs/plans/orders-and-transfers.md §4.3): the new cost,
   * applied only when newer than the last valuation (`stock_version`) and
   * than the last other cost change (`valued_at`), so a late or repeated
   * call changes nothing. Takes precedence over `cost` and
   * `weighted_receive`.
   */
  inventory_valuation?: InventoryValuation | null;
}

export interface InventoryValuation {
  /** The weighted cost per parent unit. */
  cost: number;
  /** The stock row version it was computed at. */
  stock_version: number;
  /** When it was computed. */
  valued_at: string;
  /**
   * The store product's `cost_changed_at` the valuation started from
   * (null: none). A hand edit since then wins: the valuation is skipped.
   * Optional; missing = compared by `valued_at` only.
   */
  base_changed_at?: string | null;
}

export interface WeightedReceive {
  /** Stock just before the receive, from the receive's own inventory change. */
  quantity_before: number;
  /** Parent units received. */
  quantity: number;
  /** Per-unit cost of this receive. */
  cost: number;
}

export enum CostUpdateOrigin {
  PURCHASE_ORDER_RECEIVE = 'PURCHASE_ORDER_RECEIVE',
  /** The carried cost of a transfer receipt (Orders & Transfers, PR 6). */
  TRANSFER_RECEIVE = 'TRANSFER_RECEIVE',
  /** An owner-approved cost at transfer price approval (PR 7). */
  TRANSFER_PRICE_APPROVAL = 'TRANSFER_PRICE_APPROVAL',
}

export interface UpdateStoreProductInventory {
  id: string;
  store_id: string;
  merchant_id: string;
  source_id: string;
  source_type: EntityTypes;
  inventory: number;
  /**
   * The stock buckets behind `inventory` (sellable). Optional for backwards
   * compatibility; missing = leave the stored breakdown unchanged.
   */
  stock_breakdown?: StockBreakdown;
  /**
   * Version of the stock row this snapshot was taken from. A snapshot older
   * than the one already mirrored is skipped, so a delayed update never
   * overwrites newer stock. Optional for backwards compatibility; missing =
   * always applied.
   */
  stock_version?: number;
}

export interface StoreProductPage {
  products: StoreProduct[];
  cursor?: string | null;
  count: number;
}

export interface ProductPage {
  products: Product[];
  cursor?: string;
  count: number;
}

export interface DeletedProductsPage {
  products: Product[];
  /**
   * Soft-deleted in-store product rows across all stores of the merchant.
   * Aggregated by the use-case (cursor not threaded across stores in v1).
   */
  store_products: StoreProduct[];
  cursor?: string | null;
  count: number;
}

export interface ProductFractionPage {
  fractions: ProductFraction[];
  cursor?: string;
  count: number;
}

export interface GetProductFractions {
  parent_id?: string;
  fraction_id?: string;
  cursor?: string;
  limit?: number;
  include_deleted?: SoftDeleteFlag;
}

export interface GetProductFractionsByMerchant {
  merchant_id: string;
  parent_id?: string;
  fraction_id?: string;
  cursor?: string;
  limit?: number;
  include_deleted?: SoftDeleteFlag;
}

export interface GetProductFraction {
  merchant_id: string;
  id: string;
  include_deleted?: SoftDeleteFlag;
}

export interface CreateProductFraction {
  fraction_id: string;
  parent_id: string;
  merchant_id: string;
  creator_id: string;
}

export interface UpdateProductFraction {
  id: string;
  parent_id?: string;
  fraction_name?: string;
  parent_name?: string;
  merchant_id: string;
  updator_id: string;
}

export interface RepairParentFractions {
  parent_id: string;
  merchant_id: string;
  updator_id: string;
}

export interface RepairParentFractionsResult {
  parent_id: string;
  parent_name: string;
  parent_quantity_in_unit_package: number;
  children_repaired: number;
  children_already_correct: number;
  store_fractions_repaired: number;
  stores_notified: number;
}

export interface SKU extends Timestamps {
  global_sku: number;
  merchant_id: string;
  unique_by_merchant: string;
  product_id: string;
}

export enum StoreProductChangeEvents {
  STORE_PRODUCT_SOLD_ONLINE_UPDATED = 'STORE_PRODUCT_SOLD_ONLINE_UPDATED',
  STORE_PRODUCT_DISPLAY_COST_ON_POS_UPDATED = 'STORE_PRODUCT_DISPLAY_COST_ON_POS_UPDATED',
  STORE_PRODUCT_CREATED = 'STORE_PRODUCT_CREATED',
  STORE_SKU_UPDATED = 'STORE_SKU_UPDATED',
  STORE_PRODUCT_IS_AVAILABLE_IN_STORE_UPDATED = 'STORE_PRODUCT_IS_AVAILABLE_IN_STORE_UPDATED',
  STORE_PRODUCT_SOLD_BY_WEIGHT_UPDATED = 'STORE_PRODUCT_SOLD_BY_WEIGHT_UPDATED',
  STORE_PRODUCT_INVENTORY_UPDATED = 'STORE_PRODUCT_INVENTORY_UPDATED',
  STORE_PRODUCT_PRICE_UPDATED = 'STORE_PRODUCT_PRICE_UPDATED',
  STORE_PRODUCT_COST_UPDATED = 'STORE_PRODUCT_COST_UPDATED',
  STORE_PRODUCT_LOW_STOCK_THRESHOLD_UPDATED = 'STORE_PRODUCT_LOW_STOCK_THRESHOLD_UPDATED',
  STORE_PRODUCT_EXPIRY_WARNING_DAYS_UPDATED = 'STORE_PRODUCT_EXPIRY_WARNING_DAYS_UPDATED',
  STORE_PRODUCT_REORDER_LEVEL_UPDATED = 'STORE_PRODUCT_REORDER_LEVEL_UPDATED',
  STORE_PRODUCT_IS_AVAILABLE_FOR_SALE_UPDATED = 'STORE_PRODUCT_IS_AVAILABLE_FOR_SALE_UPDATED',
  STORE_PRODUCT_IS_SOLD_BY_RANGE_UPDATED = 'STORE_PRODUCT_IS_SOLD_BY_RANGE_UPDATED',
  STORE_PRODUCT_IS_SOLD_BY_WEIGHT_UPDATED = 'STORE_PRODUCT_IS_SOLD_BY_WEIGHT_UPDATED',
  STORE_PRODUCT_IS_SOLD_ONLINE_UPDATED = 'STORE_PRODUCT_IS_SOLD_ONLINE_UPDATED',
  STORE_PRODUCT_MANUAL_CONTROL_UPDATED = 'STORE_PRODUCT_MANUAL_CONTROL_UPDATED',
  STORE_PRODUCT_MAX_SELLING_PRICE_UPDATED = 'STORE_PRODUCT_MAX_SELLING_PRICE_UPDATED',
  STORE_PRODUCT_MIN_SELLING_PRICE_UPDATED = 'STORE_PRODUCT_MIN_SELLING_PRICE_UPDATED',
  STORE_PRODUCT_LOYALTY_PROGRAMS_UPDATED = 'STORE_PRODUCT_LOYALTY_PROGRAMS_UPDATED',
  STORE_PRODUCT_DELETED = 'STORE_PRODUCT_DELETED',
  STORE_PRODUCT_RESTORED = 'STORE_PRODUCT_RESTORED',
  STORE_PREFIX_UPDATED = 'STORE_PREFIX_UPDATED',
  STORE_PRODUCT_FRACTIONS_UPDATED = 'STORE_PRODUCT_FRACTIONS_UPDATED',
  STORE_PRODUCT_POPULARITY_SCORE_UPDATED = 'STORE_PRODUCT_POPULARITY_SCORE_UPDATED',
  STORE_PRODUCT_ALLOW_BELOW_COST_SALE_UPDATED = 'STORE_PRODUCT_ALLOW_BELOW_COST_SALE_UPDATED',
  STORE_PRODUCT_DISCOUNT_ASSIGNED = 'STORE_PRODUCT_DISCOUNT_ASSIGNED',
  STORE_PRODUCT_DISCOUNT_REASSIGNED = 'STORE_PRODUCT_DISCOUNT_REASSIGNED',
  STORE_PRODUCT_DISCOUNT_UNASSIGNED = 'STORE_PRODUCT_DISCOUNT_UNASSIGNED',
  STORE_PRODUCT_DISCOUNT_UPDATED = 'STORE_PRODUCT_DISCOUNT_UPDATED',
  STORE_PRODUCT_DISCOUNT_EXPIRED = 'STORE_PRODUCT_DISCOUNT_EXPIRED',
  STORE_PRODUCT_DISCOUNT_DELETED = 'STORE_PRODUCT_DISCOUNT_DELETED',
  STORE_PRODUCT_DISCOUNTED_PRICE_UPDATED = 'STORE_PRODUCT_DISCOUNTED_PRICE_UPDATED',
}

export type StoreProductDBItem = StoreProductAttributes & {
  PK: string; // PK = `PRODUCT#${product_id}`
  SK: string; // SK = `STORE#${store_id}`
  GSI1PK: string; // GSI1PK = `STORE#${store_id}`
  GSI1SK: string; // GSI1SK = `PRODUCT#${product_id}`
  GSI2PK: string; // GSI2PK = `IS_PARENT#${is_parent}`
  GSI2SK: string; // GSI2SK = `STORE#${id}#PRODUCT#${id}`
  EntityIndexPK: string; // EntityIndexPK = `STORE_PRODUCT`
  EntityIndexSK: string; // EntityIndexSK = `PRODUCT#${product_id}#STORE#${store_id}`
};

export interface ProductDBItem extends Product {
  PK: string; // PK = `PRODUCT#${id}`
  SK: string; // SK = `PRODUCT#${id}`
  GSI1PK: string; // GSI1PK = `MERCHANT#${id}`
  GSI1SK: string; // GSI1SK = `PRODUCT#${id}`
  GSI2PK: string; // GSI2PK = `IS_PARENT#${is_parent}`
  GSI2SK: string; // GSI2SK = `MERCHANT#${id}#PRODUCT#${id}`
  EntityIndexPK: string; // EntityIndexPK = `PRODUCT`
  EntityIndexSK: string; // EntityIndexSK = `MERCHANT#${merchant_id}#DATE#${createdAt}`
}

export interface ProductFractionDBItem extends ProductFraction {
  PK: string; // PK = `PRODUCT#${product_id}`
  SK: string; // SK = `PRODUCT_FRACTION#${id}`
  GSI1PK: string; // GSI1PK = `MERCHANT#${id}`
  GSI1SK: string; // GSI1SK = `PRODUCT_FRACTION#${id}`
  GSI2PK: string; // GSI2PK = `PRODUCT_FRACTION#${id}`
  GSI2SK: string; // GSI2SK = `PRODUCT#${product_id}`
  EntityIndexPK: string; // EntityIndexPK = `PRODUCT_FRACTION`
  EntityIndexSK: string; // EntityIndexSK = `PRODUCT#${product_id}#PRODUCT_FRACTION#${id}`
}

export interface SKUDBItem extends SKU {
  // PK = MERCHANT#{merchant_id}
  // SK = GLOBAL_SKU#{sku}
  // GSI1PK = GLOBAL_SKU#{sku}
  // GSI1SK = MERCHANT#{merchant_id}
  // GSI2PK = GLOBAL_SKU#{sku}
  // GSI2SK = PRODUCT#{product_id}
  // GSI3PK = unique_by_merchant
  // GSI3SK = GLOBAL_SKU#{sku}
  // EntityIndexPK = GLOBAL_SKU
  // EntityIndexSK = MERCHANT#{merchant_id}#PRODUCT#{product_id}#GLCOBAL_SKU#{sku}

  PK: string;
  SK: string;
  GSI1PK: string;
  GSI1SK: string;
  GSI2PK: string;
  GSI2SK: string;
  GSI3PK: string;
  GSI3SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

export interface ChangeEvent {
  changes: ChangedAttributes;
  event: ProductChangeEvents | StoreProductChangeEvents;
}

export enum ProductChangeEvents {
  PRODUCT_CONVERTED_TO_PARENT = 'PRODUCT_CONVERTED_TO_PARENT',
  PRODUCT_CONVERTED_TO_CHILD = 'PRODUCT_CONVERTED_TO_CHILD',
  PRODUCT_CREATED = 'PRODUCT_CREATED',
  PRODUCT_NAME_UPDATED = 'PRODUCT_NAME_UPDATED',
  PRODUCT_DESCRIPTION_UPDATED = 'PRODUCT_DESCRIPTION_UPDATED',
  PRODUCT_IMAGE_UPDATED = 'PRODUCT_IMAGE_UPDATED',
  PRODUCT_BARCODE_UPDATED = 'PRODUCT_BARCODE_UPDATED',
  PRODUCT_QUANTITY_IN_UNIT_PACKAGE_UPDATED = 'PRODUCT_QUANTITY_IN_UNIT_PACKAGE_UPDATED',
  PRODUCT_GLOBAL_SKU_UPDATED = 'PRODUCT_GLOBAL_SKU_UPDATED',
  PRODUCT_WEIGHT_KG_UPDATED = 'PRODUCT_WEIGHT_KG_UPDATED',
  PRODUCT_UNIT_UPDATED = 'PRODUCT_UNIT_UPDATED',
  PRODUCT_CATEGORY_UPDATED = 'PRODUCT_CATEGORY_UPDATED',
  PRODUCT_FRACTION_UPDATED = 'PRODUCT_FRACTION_UPDATED',
  PRODUCT_PARENT_UPDATED = 'PRODUCT_PARENT_UPDATED',
  PRODUCT_TAXES_UPDATED = 'PRODUCT_TAXES_UPDATED',
  PRODUCT_DELETED = 'PRODUCT_DELETED',
  PRODUCT_RESTORED = 'PRODUCT_RESTORED',
  PRODUCT_FRACTIONS_UPDATED = 'PRODUCT_FRACTIONS_UPDATED',
  PRODUCT_DELETED_AT_UPDATED = 'PRODUCT_DELETED_AT_UPDATED',
  PRODUCT_CATEGORY_ID_UPDATED = 'PRODUCT_CATEGORY_ID_UPDATED',
  PRODUCT_UNIT_OF_MEASUREMENT_UPDATED = 'PRODUCT_UNIT_OF_MEASUREMENT_UPDATED',
  PRODUCT_BARCODES_UPDATED = 'PRODUCT_BARCODES_UPDATED',
}

export interface ProductChange extends Timestamps {
  event: ProductChangeEvents;
  version: string;
  updator_id: string;
  changed_attributes: ChangedAttributes;
  change_reason: string;
  id: string;
  merchant_id: string;
  updator_name: string;
  product_name: string;
}

export interface ProductChangeDBItem extends ProductChange {
  // PK = `PRODUCT#${id}`
  // SK = `PRODUCT_VERSION#${padded_version}`
  // GSI1PK = `MERCHANT#${merchant_id}`
  // GSI1SK = `PRODUCT_CHANGE#${id}#DATE#${createdAt}`
  // GSI2PK = `USER#${updator_id}`
  // GSI2SK = `PRODUCT_CHANGE#${id}#DATE#${createdAt}`
  // GSI3PK = `MERCHANT#${merchant_id}#EVENT#${event}`
  // GSI3SK = `PRODUCT_CHANGE#${id}#DATE#${createdAt}`
  // GSI4PK = `MERCHANT#${merchant_id}#EVENT#${event}`
  // GSI4SK = `PRODUCT_CHANGE#DATE#${createdAt}`
  // GSI5PK = `USER#${updator_id}`
  // GSI5SK = `PRODUCT_CHANGE#DATE#${createdAt}`
  // GSI6PK = `USER#${updator_id}#EVENT#${event}`
  // GSI6SK = `PRODUCT_CHANGE#${id}#DATE#${createdAt}`
  // EntityIndexPK = `PRODUCT_CHANGE`
  // EntityIndexSK = `MERCHANT#${merchant_id}#DATE#${createdAt}`
  PK: string;
  SK: string;
  GSI1PK: string;
  GSI1SK: string;
  GSI2PK: string;
  GSI2SK: string;
  GSI3PK: string;
  GSI3SK: string;
  GSI4PK: string;
  GSI4SK: string;
  GSI5PK: string;
  GSI5SK: string;
  GSI6PK: string;
  GSI6SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

export interface ProductChangesPage {
  changes: ProductChange[];
  cursor?: string;
  count: number;
}

export interface GetProductChanges {
  /**
   * Query all product changes made by a specific user.
   *
   * **How to Query:**
   * - **Index:** GSI2
   * - **GSI2PK:** `USER#${updator_id}`
   * - **GSI2SK:** starts with `PRODUCT_CHANGE#`
   */
  by_user?: boolean;

  /**
   * Query all product changes made by an user within a date range.
   *
   * **How to Query:**
   * - **Index:** GSI5
   * - **GSI5PK:** `USER#${updator_id}`
   * - **GSI5SK Range:** Between `PRODUCT_CHANGE#DATE#${start_date}` and `PRODUCT_CHANGE#DATE#${end_date}`
   */
  by_user_and_date_range?: boolean;
  /**
   * Query By user and product
   * - **Index:** GSI2
   * - **GSI6PK:** `USER#${updator_id}`
   * - **GSI6SK:** starts with `PRODUCT_CHANGE#${product_id}`
   */

  by_user_and_product?: boolean;

  /**
   * Query By user and product within a date range
   * - **Index:** GSI2
   * - **GSI6PK:** `USER#${updator_id}`
   * - **GSI6SK Range:** Between `PRODUCT_CHANGE#${product_id}#DATE#${start_date}` and `PRODUCT_CHANGE#${product_id}#DATE#${end_date}`
   * - **Note:** Requires `start_date` and `end_date`
   * - **Note:** Requires `updator_id`
   */
  by_user_product_and_date_range?: boolean;

  /**
   * Query By user and event type
   * - **Index:** GSI7
   * - **GSI7PK:** `USER#${updator_id}#EVENT#${event}`
   * - **GSI7SK:** starts with `PRODUCT_CHANGE#`
   */
  by_user_and_event?: boolean;

  /**
   * Query By user and event type and product within a date range
   * - **Index:** GSI7
   * - **GSI7PK:** `USER#${updator_id}#EVENT#${event}`
   * - **GSI7SK Range:** Between `PRODUCT_CHANGE#${id}#DATE#${start_date}` and `PRODUCT_CHANGE#DATE#${end_date}`
   * - **Note:** Requires `start_date` and `end_date`
   * - **Note:** Requires `updator_id`
   */
  by_user_and_product_and_event_and_date_range?: boolean;

  /**
   * Query all versions of a specific product.
   *
   * **How to Query:**
   * - **Table:** Main Table
   * - **Partition Key (PK):** `PRODUCT#${product_id}`
   * - **Sort Key (SK):** `PRODUCT_VERSION#${padded_version}`
   */
  by_product?: boolean;

  /**
   * Query all product changes for a specific merchant.
   *
   * **How to Query:**
   * - **Index:** GSI1
   * - **GSI1PK:** `MERCHANT#${merchant_id}`
   * - **GSI1SK:** starts with "PRODUCT_CHANGE#"
   */
  by_merchant?: boolean;

  /**
   * Query all changes for a specific product within a merchant.
   *
   * **How to Query:**
   * - **Index:** GSI1
   * - **GSI1PK:** `MERCHANT#${merchant_id}`
   * - **GSI1SK:** starts with 'PRODUCT_CHANGE#${product_id}'
   */
  by_merchant_and_product?: boolean;

  /**
   * Query all product changes for a merchant within a date range.
   *
   * **How to Query:**
   * - **Index:** EntityIndex
   * - **EntityIndexPK:** `PRODUCT_CHANGE`
   * - **EntityIndexSK Range:** Between `MERCHANT#${id}#DATE#${start_date}` and `MERCHANT#${id}#DATE#${end_date}`
   */
  by_merchant_and_date_range?: boolean;

  /**
   * Query all changes for a specific product under a merchant within a date range.
   *
   * **How to Query:**
   * - **Index:** GSI1
   * - **GSI1PK:** `MERCHANT#${merchant_id}`
   * - **GSI1SK Range:** Between `PRODUCT_CHANGE#${product_id}#DATE#${start_date}` and `PRODUCT_CHANGE#${product_id}#DATE#${end_date}`
   */
  by_merchant_product_and_date_range?: boolean;

  /**
   * Query all product changes of a specific event type for a merchant.
   *
   * **How to Query:**
   * - **Index:** GSI3
   * - **GSI3PK:** `MERCHANT#${merchant_id}#EVENT#${event}`
   * - **GSI3SK:** starts with `PRODUCT_CHANGE#`
   */
  by_merchant_and_event?: boolean;

  /**
   * Query all product changes of a specific event type for a merchant within a date range.
   *
   * **How to Query:**
   * - **Index:** GSI4
   * - **GSI4PK:** `MERCHANT#${merchant_id}#EVENT#${event}`
   * - **GSI4SK Range:** Between `PRODUCT_CHANGE#DATE#${start_date}` and `PRODUCT_CHANGE#DATE#${end_date}`
   */
  by_merchant_event_and_date_range?: boolean;

  /**
   * Query all changes by merchant and event type and product
   * - **Index:** GSI3
   * - **GSI3PK:** `MERCHANT#${merchant_id}#EVENT#${event}`
   * - **GSI3SK:** starts with `PRODUCT_CHANGE#${product_id}`
   */
  by_merchant_event_and_product?: boolean;

  /**
   * Query all changes by merchant and event type and product within a date range
   * - **Index:** GSI3
   * - **GSI3PK:** `MERCHANT#${merchant_id}#EVENT#${event}`
   * - **GSI3SK Range:** Between `PRODUCT_CHANGE#${product_id}#DATE#${start_date}` and `PRODUCT_CHANGE#${product_id}#DATE#${end_date}`
   */
  by_merchant_event_product_and_date_range?: boolean;

  /**
   * Query all product changes by product and version range.
   *
   * **How to Query:**
   * - **Table:** Main Table
   * - **PK:** `PRODUCT#${product_id}`
   * - **SK Range:** Between `PRODUCT_VERSION#${start_version}` and `PRODUCT_VERSION#${end_version}`
   * - **Note:** Requires that `padded_version`
   */
  by_product_and_version_range?: boolean;

  // Query parameters
  limit?: number;
  cursor?: string;
  /**
   * Start date for date range filtering.
   * - **Format:** ISO 8601 string (e.g., '2023-09-24T12:34:56Z')
   */
  start_date?: string;

  /**
   * End date for date range filtering.
   * - **Format:** ISO 8601 string
   */
  end_date?: string;

  /**
   * Merchant ID.
   * - Required for queries involving merchants.
   */
  merchant_id: string;

  /**
   * Product ID.
   * - Required for queries involving specific products.
   */
  product_id?: string;

  /**
   * Event type.
   * - Enum of possible product change events.
   */
  event?: ProductChangeEvents;

  /**
   * user (updator) ID.
   * - Required for queries involving specific users.
   */
  updator_id?: string;

  sort_order?: 'ASC' | 'DESC';

  /**
   * Product version.
   * - Required for queries involving specific product versions.
   */
  version?: number;

  /**
   * Start version for version range filtering.
   */
  start_version?: string;

  /**
   * End version for version range filtering.
   */
  end_version?: string;
}

export interface GetStoreProductChanges {
  /**
   * Query all store_product changes made by a specific user.
   *
   * **How to Query:**
   * - **Index:** GSI2
   * - **GSI2PK:** `USER#${updator_id}`
   * - **GSI2SK:** starts with `STORE_PRODUCT_CHANGE#`
   */
  by_user?: boolean;

  /**
   * Query all store_product changes made by an user within a date range.
   *
   * **How to Query:**
   * - **Index:** GSI5
   * - **GSI5PK:** `USER#${updator_id}`
   * - **GSI5SK Range:** Between `STORE_PRODUCT_CHANGE#DATE#${start_date}` and `STORE_PRODUCT_CHANGE#DATE#${end_date}`
   */
  by_user_and_date_range?: boolean;
  /**
   * Query By user and store_product
   * - **Index:** GSI2
   * - **GSI6PK:** `USER#${updator_id}`
   * - **GSI6SK:** starts with `STORE_PRODUCT_CHANGE#${product_id}`
   */

  by_user_and_store_product?: boolean;
  by_user_store_product_and_date_range?: boolean;
  by_user_and_event?: boolean;
  by_user_and_store_product_and_event?: boolean;
  by_user_and_store_product_and_event_and_date_range?: boolean;
  by_store_product?: boolean;
  by_store?: boolean;
  by_store_and_store_product?: boolean;
  by_store_and_date_range?: boolean;
  by_store_and_store_product_and_date_range?: boolean;
  by_store_and_event?: boolean;
  by_store_and_event_and_date_range?: boolean;
  by_store_event_and_store_product?: boolean;
  by_store_event_store_product_and_date_range?: boolean;
  by_store_product_and_version_range?: boolean;
  limit?: number;
  cursor?: string;
  start_date?: string;
  end_date?: string;
  store_id?: string;
  product_id?: string;
  event?: ProductChangeEvents | StoreProductChangeEvents;
  updator_id?: string;
  sort_order?: 'ASC' | 'DESC';
  version?: number;
  start_version?: number;
  end_version?: number;
}

export interface StoreProductChangesPage {
  changes: StoreProductChange[];
  cursor?: string;
  count: number;
}

export interface StoreProductChange extends Timestamps {
  event: StoreProductChangeEvents;
  version: number;
  updator_id: string;
  changed_attributes: ChangedAttributes;
  change_reason: string;
  id: string;
  store_id: string;
  merchant_id: string;
  updator_name: string;
  product_name: string;
  /**
   * On-hand stock (in parent units) at the moment of a cost edit.
   * Recorded only on STORE_PRODUCT_COST_UPDATED events for parent
   * products whose cost was edited manually — not for the weighted-cost
   * update that follows a purchase order receive.
   */
  inventory_at_change?: number;
  /**
   * inventory_at_change × (new cost − old cost): how much this cost edit
   * revalued the stock already on hand. Summed into the Valuation
   * dashboard's "Cost Changes" breakdown row so the Est. Total
   * difference reconciles with the change breakdown.
   */
  inventory_value_change?: number;
}

export interface StoreProductChangeDBItem extends StoreProductChange {
  PK: string;
  SK: string;
  GSI1PK: string;
  GSI1SK: string;
  GSI2PK: string;
  GSI2SK: string;
  GSI3PK: string;
  GSI3SK: string;
  GSI4PK: string;
  GSI4SK: string;
  GSI5PK: string;
  GSI5SK: string;
  GSI6PK: string;
  GSI6SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

export interface BulkImportStoreProduct {
  store_id: string;
  products: string[];
  merchant_id: string;
  creator_id: string;
  creator_type: EntityTypes;
}

export interface BulkCreateStoreProductsResponse {
  message: string;
}

export interface StoreProductWebSocketMessage {
  type: EntityTypes;
  message: StoreProduct;
}

/**
 * Body of POST /products/{merchant_id}/store-products/{store_id}/deleted-ids:
 * the POS asks which of the products it holds were deleted.
 */
export interface GetDeletedStoreProductIds {
  /** Store product ids held by the POS (at most 500 per request). */
  ids: string[];
}

/** A soft-deleted store product, for the POS to remove its copy. */
export interface DeletedStoreProduct {
  id: string;
  /**
   * When it was deleted: the POS keeps a copy updated after this (it was
   * restored meanwhile).
   */
  deleted_at: string;
}

/** The store products, among those asked, that are soft-deleted. */
export interface DeletedStoreProducts {
  deleted: DeletedStoreProduct[];
}
